<template>
    <div ref="mapContainer" class="w-full h-[calc(100%-3rem)] relative">
    <!-- Map will render here -->
      
      <!-- Use reusable error overlay component -->
      <ErrorOverlay 
        :show="showErrorOverlay"
        icon="error_outline"
        :message="errorMessage"
      />
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, nextTick, onUnmounted, PropType, toRaw, computed } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { VitalRecordData } from '../../models/vitalRecordData';
import ErrorOverlay from '../../common/ErrorOverlay.vue'; // Import ErrorOverlay

// Replace the current TrackingPoint interface with this simplified version
interface TrackingPoint {
  timestamp: number;
  location?: {
    lat: number;
    lon: number;
  };
  hsi?: number;            // Heat strain index
  heartRate?: number;      // Heart rate
  bodyTemp?: number;       // Body temperature
  skinTemp?: number;       // Skin temperature
}

export default defineComponent({
  name: 'MapComponent',
  components: {
    ErrorOverlay // Register the component
  },
  props: {
    mode: {
      type: String as PropType<'NewestLocationInTimeRange' | 'TrackInTimeRange'>,
      default: 'NewestLocationInTimeRange'
    },
    mapNewestData: {
      type: Object as PropType<VitalRecordData | null>,
      required: false,
      default: () => null
    },
    mapTrackingData: {
      type: Array as PropType<TrackingPoint[]>,
      default: () => []
    },
    showErrorOverlay: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const mapContainer = ref<HTMLElement | null>(null);
    const map = ref<L.Map | null>(null);
    const markers = ref<L.Marker[]>([]);
    const trackingLayer = ref<L.LayerGroup | null>(null);
    // Create a named function for the resize handler so we can properly remove it
    const handleResize = () => {
      // Only call invalidateSize if map exists and is properly initialized
      if (map.value && map.value.getContainer()) {
        try {
          // Use toRaw to unwrap the map from Vue's reactivity system
          const rawMap = toRaw(map.value);
          if (rawMap && (rawMap as any)._leaflet_id) {
            rawMap.invalidateSize();
          }
        } catch (e) {
          console.warn('Error resizing map:', e);
        }
      }
    };
    
    // Simplified color and zone handling
    const getHsiColor = (hsi: number | null): string => {
      if (hsi === null) return '#D3D3D3';
      if (hsi < 1) return '#90C590';
      if (hsi < 3) return '#E6D690';
      if (hsi < 7) return '#E6A67C';
      return '#E68A8A';
    };
    
    const createMarker = (record: VitalRecordData) => {
      const color = getHsiColor(record.heat_strain_index);
      const icon = L.divIcon({
        html: `<div style="background-color: ${color}; width: 18px; height: 18px; border-radius: 50%; border: 2px solid white;"></div>`,
        className: 'custom-circle-marker',
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      });
      
      const marker = L.marker([record.location.lat, record.location.lon], { icon });
      
      // Format date in Japanese style
      const date = new Date(record.check_timestamp);
      const dateString = date.toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '年').replace(/\//g, '月') + '日';
      
      const timeString = date.toLocaleTimeString('ja-JP', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      
      // Create unified popup content
      const popupContent = `
        <div class="p-2">
          <p class="mb-1"><strong>日付：</strong>${dateString}</p>
          <p class="mb-1"><strong>時間：</strong>${timeString}</p>
          <p class="mb-1"><strong>HSI：</strong>${record.heat_strain_index?.toFixed(1) || '-'}</p>
          <p class="mb-1"><strong>心拍数：</strong>${record.heart_rate || '-'} bpm</p>
          <p class="mb-1"><strong>深部体温：</strong>${record.body_temperature?.toFixed(1) || '-'}℃</p>
          <p class="mb-1"><strong>皮膚温度：</strong>${record.skin_temperature?.toFixed(1) || '-'}℃</p>
        </div>
      `;
      
      marker.bindPopup(popupContent, {
        closeButton: false,
        className: 'custom-popup',
        autoPan: false,
        offset: [0, -12]
      });
      
      return marker;
    };
    
    const updateMap = () => {
      if (!map.value || !mapContainer.value) return;
      
      // Use toRaw to unwrap the map from Vue's reactivity system
      const rawMap = toRaw(map.value);
      
      // Clear existing markers
      markers.value.forEach(marker => {
        const rawMarker = toRaw(marker);
        rawMarker.remove();
      });
      markers.value = [];
      
      if (!props.mapNewestData?.location) return;
      
      const marker = createMarker(props.mapNewestData);
      // Use the raw map when adding the marker
      marker.addTo(rawMap as L.Map);
      markers.value.push(marker);
      
      // Use the raw map for setView
      (rawMap as L.Map).setView([props.mapNewestData.location.lat, props.mapNewestData.location.lon], 15);
    };
    
    const initMap = () => {
      if (!mapContainer.value) return;
      
      try {
        // Create map with attribution control disabled
        const newMap = L.map(mapContainer.value, {
          zoomAnimation: true,
          fadeAnimation: true,
          markerZoomAnimation: true,
          inertia: true,
          attributionControl: false, // Disable default attribution
          zoomControl: true,
          minZoom: 5,
          maxZoom: 18
        }).setView([35.6810, 139.7670], 13);
        
        // Add tile layer without attribution
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(newMap);
        
        // Create a custom control with only OpenStreetMap attribution
        const CustomAttributionControl = L.Control.extend({
          options: {
            position: 'bottomright'
          },
          onAdd: function() {
            const div = L.DomUtil.create('div', 'leaflet-control-attribution');
            div.innerHTML = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
            return div;
          }
        });
        
        // Add the control to the map
        new CustomAttributionControl().addTo(newMap);
        
        // Store the raw map reference
        map.value = newMap;
        
        // Add error handling for zoom events
        toRaw(map.value).on('zoomstart', () => {
        markers.value.forEach(marker => {
            try {
              // Use toRaw to get the actual marker instance
              toRaw(marker).closePopup();
            } catch (err) {
              console.warn('Error handling zoom start:', err);
            }
          });
        });
        
        // In the initMap function, add this after creating the map
        trackingLayer.value = L.layerGroup().addTo(toRaw(map.value) as L.Map);
        
        updateMap();
      } catch (err) {
        // console.error('Map initialization error:', err);
      }
    };
    
    // Update the operator marker
    const updateMarker = () => {
      if (!map.value) return;

      // Clear previous markers
      markers.value.forEach(marker => {
        if (marker) {
          const rawMarker = toRaw(marker);
          rawMarker.remove();
        }
      });
      markers.value = [];

      // Add new marker if we have data
      if (props.mapNewestData && props.mapNewestData.location) {
        const marker = createMarker(props.mapNewestData);
        marker.addTo(toRaw(map.value) as L.Map);
        markers.value.push(marker);
        
        // Center map on marker
        const rawMap = toRaw(map.value);
        rawMap.setView([props.mapNewestData.location.lat, props.mapNewestData.location.lon], 15);
      }
    };

    // Then update the drawTrackingPath function to use this simpler structure
    const drawTrackingPath = (trackingData: TrackingPoint[]) => {
      // First remove the existing tracking layer completely 
      if (trackingLayer.value) {
        try {
          // Use any to bypass TypeScript checks
          const rawMap = toRaw(map.value) as any;
          const rawLayer = toRaw(trackingLayer.value) as any;
          rawMap.removeLayer(rawLayer);
          trackingLayer.value = null;
        } catch (e) {
          // console.error("Failed to remove tracking layer:", e);
        }
      }
      
      // Create a fresh layer group and add it to map
      const rawMap = toRaw(map.value) as any;
      trackingLayer.value = L.layerGroup();
      const rawLayer = toRaw(trackingLayer.value) as any;
      rawMap.addLayer(rawLayer);
      
      // Filter out points without location
      const validPoints = trackingData
        .filter(point => point.location)
        .sort((a, b) => a.timestamp - b.timestamp);
      
      if (validPoints.length === 0) return;
      
      // Create polyline connecting all points
      L.polyline(
        validPoints.map(p => [p.location!.lat, p.location!.lon] as [number, number]),
        { 
          color: '#3388ff',
          weight: 3,
          opacity: 0.7
        }
      ).addTo(rawLayer);
      
      // Add markers for each point
      validPoints.forEach((point, index) => {
        // Ensure point has location before creating marker
        if (!point.location) return;
        
        // Determine marker style based on position
        let iconColor = '#3388ff'; // Default blue
        if (index === 0) iconColor = '#4CAF50'; // First point: green
        else if (index === validPoints.length - 1) iconColor = '#FF5252'; // Last point: red
        
        // Use HSI for color if available
        if (point.hsi !== undefined) {
          iconColor = getHsiColor(point.hsi);
        }
        
        // Create custom icon
        const icon = L.divIcon({
          html: `<div style="background-color: ${iconColor}; width: 14px; height: 14px; border-radius: 50%; border: 2px solid white;"></div>`,
          className: 'custom-circle-marker',
          iconSize: [18, 18],
          iconAnchor: [9, 9]
        });
        
        // Create marker with popup - add the same config options
        L.marker([point.location.lat, point.location.lon], { icon })
          .bindPopup(() => {
            // Format date in Japanese style
            const date = new Date(point.timestamp);
            const dateString = date.toLocaleDateString('ja-JP', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            }).replace(/\//g, '年').replace(/\//g, '月') + '日';
            
            const timeString = date.toLocaleTimeString('ja-JP', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            });
            
            return `
            <div class="p-2">
                <p class="mb-1"><strong>日付：</strong>${dateString}</p>
                <p class="mb-1"><strong>時間：</strong>${timeString}</p>
                <p class="mb-1"><strong>HSI：</strong>${point.hsi?.toFixed(1) || '-'}</p>
                <p class="mb-1"><strong>心拍数：</strong>${point.heartRate?.toFixed(0) || '-'} bpm</p>
                <p class="mb-1"><strong>深部体温：</strong>${point.bodyTemp?.toFixed(1) || '-'}℃</p>
                <p class="mb-1"><strong>皮膚温度：</strong>${point.skinTemp?.toFixed(1) || '-'}℃</p>
            </div>
          `;
          }, {
            closeButton: false,
            className: 'custom-popup',
            autoPan: false,
            offset: [0, -12]
          })
          .addTo(rawLayer);
      });
      
      // Fit map to show all tracking points
      if (validPoints.length > 1) {
        const bounds = L.latLngBounds(validPoints.map(p => [p.location!.lat, p.location!.lon] as [number, number]));
        if (bounds.isValid()) {
          rawMap.fitBounds(bounds);
        }
      } else if (validPoints.length === 1) {
        rawMap.setView([validPoints[0].location!.lat, validPoints[0].location!.lon], 15);
      }
    };

    // Update clearAll to use the same casting approach for markers
    const clearAll = () => {
      // Clear all markers with casting
      markers.value.forEach(marker => {
        try {
          const rawMap = toRaw(map.value) as any;
          const rawMarker = toRaw(marker) as any;
          rawMap.removeLayer(rawMarker);
        } catch (e) {
          // console.error("Failed to remove marker:", e);
        }
      });
      markers.value = [];
      
      // Remove tracking layer directly from map
      if (trackingLayer.value) {
        try {
          const rawMap = toRaw(map.value) as any;
          const rawLayer = toRaw(trackingLayer.value) as any;
          rawMap.removeLayer(rawLayer);
          trackingLayer.value = null;
        } catch (e) {
          // console.error("Failed to remove tracking layer:", e);
        }
      }
    };

    // Add a ref to track the selection type
    const selectionType = ref<'single' | 'multi' | null>(null);

    // Add a computed property for the error message
    const errorMessage = computed(() => {
      return props.mode === 'TrackInTimeRange'
        ? "選択された期間にはトラッキングデータがありません" 
        : "この時間範囲内でセンサーの位置情報を見つけることができません";
    });

    // Update watch handlers to track selection type
    watch(() => props.mapNewestData, (newVal) => {
        if (newVal) {
            selectionType.value = 'single';
        }
    });

    watch(() => props.mapTrackingData, (newVal) => {
        if (newVal) {
            selectionType.value = 'multi';
        }
    });

    // Use a simpler watch approach
    watch(() => props.mode, () => {
      // Reset state when mode changes
      clearAll();
    });

    // Update the display logic to be mode-aware
    watch([() => props.mapTrackingData, () => props.mapNewestData, () => props.mode], () => {
      nextTick(() => {
        // Always start with a clean slate
        clearAll();
        
        // Then decide what to show based on mode
        if (props.mode === 'TrackInTimeRange' && props.mapTrackingData && props.mapTrackingData.length > 0) {
          drawTrackingPath(props.mapTrackingData);
        } else if (props.mode === 'NewestLocationInTimeRange' && props.mapNewestData && props.mapNewestData.location) {
          updateMarker();
        }
      });
    }, { deep: true });
    
    onMounted(() => {
      // Initialize map on next tick to ensure DOM is ready
      nextTick(() => {
        initMap();
        // Add resize listener with named function
        window.addEventListener('resize', handleResize);
      });
    });
    
    onUnmounted(() => {
      // Remove the same function reference
      window.removeEventListener('resize', handleResize);
      
      // Clear markers first
      markers.value.forEach(marker => {
        try {
          if (marker) toRaw(marker).remove();
        } catch (e) {
          console.warn('Error removing marker on unmount:', e);
        }
      });
      markers.value = [];
      
      // Safely remove map
      if (map.value) {
        try {
          toRaw(map.value).remove();
        } catch (e) {
          console.warn('Error removing map on unmount:', e);
        }
        map.value = null;
      }
    });
    
    return {
      mapContainer,
      errorMessage
    };
  }
});
</script>

<style scoped>
/* Styles specific to the map component */
:deep(.leaflet-container) {
  font-family: inherit;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

:deep(.custom-circle-marker) {
  background: none;
  border: none;
}

:deep(.custom-circle-marker div) {
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.5), 0 2px 4px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

:deep(.leaflet-marker-icon:hover .custom-circle-marker div) {
  transform: scale(1.2);
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.6), 0 4px 8px rgba(0, 0, 0, 0.4);
}

:deep(.leaflet-popup-content) {
  margin: 8px;
  min-width: 150px;
}

:deep(.leaflet-popup-content p) {
  margin: 5px 0;
}

:deep(.custom-popup) {
  background-color: white;
  border: none;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  padding: 0;
  font-size: 14px;
}

:deep(.custom-popup .leaflet-popup-content) {
  margin: 0;
  min-width: 150px;
}

:deep(.custom-popup .leaflet-popup-content-wrapper) {
  border-radius: 4px;
  padding: 0;
}

:deep(.custom-popup .leaflet-popup-tip) {
  background-color: white;
}

:deep(.leaflet-control-attribution a[href="https://leafletjs.com"]) {
  display: none !important;
}

:deep(.leaflet-control-attribution img) {
  display: none !important;
}

.custom-div-icon {
  background: none;
  border: none;
}
</style> 